import React, { useEffect, useState } from "react";
import { FaSearch, FaSpinner } from "react-icons/fa";
import { useBoletin } from "../context/BoletinContext";
import { useUsers } from "../context/UsersContext";
import { useNotas } from "../context/NotasContext";
import { useAuth } from "../context/AuthContext";

const VisibilityToggleBoletin = () => {
  const { user } = useAuth();
  const {
    inhabilitados,
    loadingInhabilitados,
    fetchInhabilitados,
    gestionarVisibilidadBoletines,
    habilitarEstudiante,
    loading,
  } = useBoletin();

  const { students, fetchStudents, salones, fetchSalones } = useUsers();
  const { periodos, fetchPeriodos } = useNotas();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState("todos");
  const [selectedSalon, setSelectedSalon] = useState("");
  const [selectedPeriodo, setSelectedPeriodo] = useState("");
  const [selectedEstudiantes, setSelectedEstudiantes] = useState([]); // 🔥 Ahora es un array
  const [searchEstudiante, setSearchEstudiante] = useState("");
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [actionType, setActionType] = useState("habilitar");
  const [showInhabilitados, setShowInhabilitados] = useState(false);

  useEffect(() => {
    fetchStudents();
    fetchSalones();
    fetchPeriodos();
  }, []);

  useEffect(() => {
    if (filterType === "estudiante") {
      const normalize = (str) =>
        str
          ?.normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

      let filtered = students;

      if (selectedSalon) {
        filtered = filtered.filter(
          (student) => student.salon_id === parseInt(selectedSalon),
        );
      }

      if (searchEstudiante) {
        filtered = filtered.filter((student) => {
          const fullName =
            `${student.first_name} ${student.second_name} ${student.first_surname} ${student.second_surname || ""}`.toLowerCase();
          return normalize(fullName).includes(normalize(searchEstudiante));
        });
      }

      setFilteredStudents(filtered);

      // 🔥 Pre-seleccionar estudiantes inhabilitados cuando es "habilitar"
      if (actionType === "habilitar" && inhabilitados.length > 0) {
        const inhabilitadosIds = inhabilitados
          .filter((item) => item.users_id)
          .map((item) => item.users_id);

        const inhabilitadosEnLista = filtered
          .filter((student) => inhabilitadosIds.includes(student.user_id))
          .map((student) => student.user_id);

        setSelectedEstudiantes(inhabilitadosEnLista);
      }
    }
  }, [
    filterType,
    searchEstudiante,
    selectedSalon,
    students,
    actionType,
    inhabilitados,
  ]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    fetchInhabilitados();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFilterType("todos");
    setSelectedSalon("");
    setSelectedPeriodo("");
    setSelectedEstudiantes([]);
    setShowInhabilitados(false);
    setSearchEstudiante("");
    setActionType("habilitar");
  };

  // 🔥 Manejar selección de estudiantes (checkbox)
  const handleToggleEstudiante = (studentId) => {
    setSelectedEstudiantes((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // 🔥 Seleccionar/Deseleccionar todos los estudiantes visibles
  const handleToggleAll = () => {
    if (selectedEstudiantes.length === filteredStudents.length) {
      setSelectedEstudiantes([]);
    } else {
      setSelectedEstudiantes(filteredStudents.map((s) => s.user_id));
    }
  };

  const handleGestionar = async () => {
    // 🔥 Si es por estudiante y hay múltiples seleccionados, procesar uno por uno
    if (filterType === "estudiante" && selectedEstudiantes.length > 0) {
      const resultados = [];
      let exitosos = 0;
      let yaExistian = 0;
      let fallidos = 0;

      for (const studentId of selectedEstudiantes) {
        try {
          const response = await gestionarVisibilidadBoletines({
            filterType: "estudiante",
            actionType,
            selectedEstudiante: studentId,
            motivo: "Gestión manual desde panel de administración",
          });

          if (response.success) {
            if (response.alreadyExists || response.alreadyEnabled) {
              yaExistian++;
            } else {
              exitosos++;
            }
          } else {
            fallidos++;
          }

          resultados.push({
            studentId,
            success: response.success,
            message: response.message,
          });
        } catch (error) {
          fallidos++;
          resultados.push({
            studentId,
            success: false,
            message: error.message,
          });
        }
      }

      // 🔥 Mostrar resumen mejorado
      let mensaje = `Proceso completado:\n`;
      if (exitosos > 0) {
        mensaje += `✅ ${exitosos} estudiante${exitosos > 1 ? "s" : ""} ${
          actionType === "habilitar" ? "habilitado" : "inhabilitado"
        }${exitosos > 1 ? "s" : ""}\n`;
      }
      if (yaExistian > 0) {
        mensaje += `ℹ️ ${yaExistian} ya ${
          actionType === "habilitar"
            ? "estaban habilitados"
            : "estaban inhabilitados"
        }\n`;
      }
      if (fallidos > 0) {
        mensaje += `❌ ${fallidos} error${fallidos > 1 ? "es" : ""}`;
      }

      alert(mensaje);
      handleCloseModal();
      return;
    }

    // Proceso normal para "todos" o "salon"
    const data = {
      filterType,
      actionType,
      selectedSalon: filterType === "salon" ? selectedSalon : null,
      selectedPeriodo: filterType === "salon" ? selectedPeriodo : null,
      selectedEstudiante: null,
      motivo: "Gestión manual desde panel de administración",
    };

    try {
      const response = await gestionarVisibilidadBoletines(data);

      if (response.success) {
        alert(response.message);
        handleCloseModal();
      } else {
        alert(response.message || "Error al gestionar visibilidad");
      }
    } catch (error) {
      alert("Error al procesar la solicitud");
    }
  };

  const handleHabilitarEstudiante = async (estudianteId) => {
    if (
      !window.confirm("¿Deseas habilitar los boletines para este estudiante?")
    )
      return;

    try {
      const response = await habilitarEstudiante(estudianteId);

      if (response.success) {
        alert(response.message);
      } else {
        alert(response.message || "Error al habilitar estudiante");
      }
    } catch (error) {
      alert("Error al habilitar el estudiante");
    }
  };

  const isFormValid = () => {
    if (filterType === "todos") return true;
    if (filterType === "salon")
      return selectedSalon !== "" && selectedPeriodo !== "";
    if (filterType === "estudiante") return selectedEstudiantes.length > 0;
    return false;
  };

  // 🔥 Verificar si un estudiante está inhabilitado
  const isEstudianteInhabilitado = (studentId) => {
    return inhabilitados.some((item) => item.users_id === studentId);
  };

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="px-4 py-2 bg-main-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-800 transition"
      >
        Gestionar
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-blue-900">
              Gestionar Visibilidad de Boletines
            </h3>

            <div className="mb-4">
              <button
                onClick={() => {
                  setShowInhabilitados(!showInhabilitados);
                  if (!showInhabilitados) fetchInhabilitados();
                }}
                className="px-4 py-2 bg-orange text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition flex items-center gap-2"
              >
                {loadingInhabilitados && <FaSpinner className="animate-spin" />}
                {showInhabilitados
                  ? "Ocultar Inhabilitados"
                  : "Ver Inhabilitados"}
              </button>
            </div>

            {showInhabilitados && (
              <div className="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold mb-3 text-gray-700">
                  Restricciones de acceso activas:
                </h4>

                {loadingInhabilitados ? (
                  <div className="flex justify-center py-8">
                    <FaSpinner className="animate-spin text-3xl text-blue-600" />
                  </div>
                ) : inhabilitados.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No hay restricciones activas actualmente.
                  </p>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-200 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-sm font-medium">
                            Tipo
                          </th>
                          <th className="px-3 py-2 text-left text-sm font-medium">
                            Detalles
                          </th>
                          <th className="px-3 py-2 text-left text-sm font-medium">
                            Fecha
                          </th>
                          <th className="px-3 py-2 text-center text-sm font-medium">
                            Acción
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {inhabilitados.map((item) => (
                          <tr
                            key={item.id}
                            className="border-t hover:bg-gray-100"
                          >
                            <td className="px-3 py-2 text-sm">
                              {item.users_id
                                ? "Estudiante"
                                : item.salon_id
                                  ? "Salón"
                                  : "Global"}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              {item.nombre_estudiante ||
                                `${item.salon_nombre || "Todos"}${
                                  item.periodo_nombre
                                    ? ` - ${item.periodo_nombre}`
                                    : ""
                                }`}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              {new Date(item.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => {
                                  if (item.users_id) {
                                    handleHabilitarEstudiante(item.users_id);
                                  } else {
                                    alert(
                                      "Para habilitar por salón/periodo, usa el formulario abajo",
                                    );
                                  }
                                }}
                                className="px-3 py-1 bg-green text-white text-sm rounded hover:bg-green-600 transition"
                              >
                                Habilitar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Acción:
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="actionType"
                      value="habilitar"
                      checked={actionType === "habilitar"}
                      onChange={(e) => {
                        setActionType(e.target.value);
                        setSelectedEstudiantes([]);
                      }}
                      className="mr-2"
                    />
                    Habilitar
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="actionType"
                      value="inhabilitar"
                      checked={actionType === "inhabilitar"}
                      onChange={(e) => {
                        setActionType(e.target.value);
                        setSelectedEstudiantes([]);
                      }}
                      className="mr-2"
                    />
                    Inhabilitar
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Aplicar a:
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setSelectedSalon("");
                    setSelectedPeriodo("");
                    setSelectedEstudiantes([]);
                    setSearchEstudiante("");
                  }}
                >
                  <option value="todos">Todos los estudiantes</option>
                  <option value="salon">Por salón y período</option>
                  <option value="estudiante">Estudiantes específicos</option>
                </select>
              </div>

              {filterType === "salon" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Seleccionar Salón:
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      value={selectedSalon}
                      onChange={(e) => setSelectedSalon(e.target.value)}
                    >
                      <option value="">Seleccionar salón</option>
                      {salones.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salon.nombre} - {salon.grado}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Seleccionar Período:
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      value={selectedPeriodo}
                      onChange={(e) => setSelectedPeriodo(e.target.value)}
                    >
                      <option value="">Seleccionar período</option>
                      {periodos.map((periodo) => (
                        <option key={periodo.id} value={periodo.id}>
                          {periodo.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {filterType === "estudiante" && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Seleccionar Estudiantes:
                  </label>

                  <div className="mb-3">
                    <select
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      value={selectedSalon}
                      onChange={(e) => {
                        setSelectedSalon(e.target.value);
                        setSelectedEstudiantes([]);
                      }}
                    >
                      <option value="">Todos los salones</option>
                      {salones.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salon.nombre} - {salon.grado}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="relative mb-3">
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar estudiante..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-main-blue"
                      value={searchEstudiante}
                      onChange={(e) => setSearchEstudiante(e.target.value)}
                    />
                  </div>

                  {/* 🔥 Botón para seleccionar/deseleccionar todos */}
                  {filteredStudents.length > 0 && (
                    <div className="mb-3 flex justify-between items-center bg-gray-100 px-4 py-2 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">
                        {selectedEstudiantes.length} de{" "}
                        {filteredStudents.length} seleccionados
                      </span>
                      <button
                        onClick={handleToggleAll}
                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        {selectedEstudiantes.length === filteredStudents.length
                          ? "Deseleccionar todos"
                          : "Seleccionar todos"}
                      </button>
                    </div>
                  )}

                  <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-center text-sm font-medium">
                            Seleccionar
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Nombre
                          </th>
                          <th className="px-4 py-2 text-left text-sm font-medium">
                            Salón
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-medium">
                            Estado
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.length === 0 ? (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-4 py-4 text-center text-gray-500"
                            >
                              {searchEstudiante
                                ? "No se encontraron estudiantes"
                                : "Busca un estudiante"}
                            </td>
                          </tr>
                        ) : (
                          filteredStudents.map((student) => {
                            const inhabilitado = isEstudianteInhabilitado(
                              student.user_id,
                            );
                            const isSelected = selectedEstudiantes.includes(
                              student.user_id,
                            );

                            return (
                              <tr
                                key={student.user_id}
                                className={`hover:bg-gray-50 cursor-pointer ${
                                  isSelected ? "bg-blue-50" : ""
                                }`}
                                onClick={() =>
                                  handleToggleEstudiante(student.user_id)
                                }
                              >
                                <td className="px-4 py-2 text-center border-t">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                      handleToggleEstudiante(student.user_id)
                                    }
                                    className="cursor-pointer w-4 h-4"
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm border-t">
                                  {student.first_name} {student.second_name}{" "}
                                  {student.first_surname} {student.second_surname || ""}
                                </td>
                                <td className="px-4 py-2 text-sm border-t">
                                  {student.salon_nombre}
                                </td>
                                <td className="px-4 py-2 text-center border-t">
                                  {inhabilitado ? (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                      Inhabilitado
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                      Activo
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Resumen:</strong> Se{" "}
                  {actionType === "habilitar" ? "habilitarán" : "inhabilitarán"}{" "}
                  los boletines para{" "}
                  {filterType === "todos" &&
                    "todos los estudiantes del colegio"}
                  {filterType === "salon" &&
                    (selectedSalon && selectedPeriodo
                      ? `el salón ${
                          salones.find((s) => s.id === parseInt(selectedSalon))
                            ?.nombre
                        } en el período seleccionado`
                      : "el salón y período seleccionados")}
                  {filterType === "estudiante" &&
                    (selectedEstudiantes.length > 0
                      ? `${selectedEstudiantes.length} estudiante${
                          selectedEstudiantes.length > 1 ? "s" : ""
                        } seleccionado${selectedEstudiantes.length > 1 ? "s" : ""}`
                      : "los estudiantes seleccionados")}
                  .
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleGestionar}
                disabled={!isFormValid() || loading}
                className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
                  isFormValid() && !loading
                    ? actionType === "habilitar"
                      ? "bg-green hover:bg-green-600"
                      : "bg-red-500 hover:bg-red-600"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                {loading && <FaSpinner className="animate-spin" />}
                {actionType === "habilitar" ? "Habilitar" : "Inhabilitar"} (
                {selectedEstudiantes.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VisibilityToggleBoletin;
